/**
 * KHIYARSA PATCH (see PATCHES.khiyarsa.md): curated starter libraries.
 *
 * On a user's FIRST visit (empty library + flag unset) this hook seeds the
 * library panel with a small curated set of high-quality official
 * excalidraw-libraries collections, self-hosted under /libraries/ so no
 * third-party request is made and startup is unaffected (fetch happens
 * lazily, after the editor is interactive, and only once per browser).
 *
 * Users keep full control afterwards: items can be deleted, and because the
 * one-time flag persists in localStorage we never re-seed. Users can also
 * still browse and install anything from libraries.excalidraw.com — this
 * does not replace the official "Browse libraries" flow.
 */
import { useEffect } from "react";

import { loadLibraryFromBlob, mergeLibraryItems } from "@excalidraw/excalidraw";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";
import type { LibraryItems } from "@excalidraw/excalidraw/types";

import { LibraryIndexedDBAdapter } from "../data/LocalData";

/** bump the version suffix to re-seed everyone after changing the set */
const SEED_FLAG = "khiyarsa-default-libraries-v1";

/**
 * Curated seed set — small, universally useful collections only (~620 KB,
 * ~87 items). Heavy / specialized packs (AWS, GCP, Azure, K8s, BPMN, UX
 * wireframing, …) are NOT seeded; they are self-hosted under /libraries/
 * and installable on demand from /libraries/index.html via the app's
 * native #addLibrary flow.
 */
const LIBRARY_FILES: string[] = [
  "/libraries/software-architecture.excalidrawlib",
  "/libraries/system-design-components.excalidrawlib",
  "/libraries/architecture-diagram-components.excalidrawlib",
  "/libraries/flow-chart-symbols.excalidrawlib",
  "/libraries/uml-er-shapes.excalidrawlib",
  "/libraries/stick-figures.excalidrawlib",
];

/**
 * Same-origin curated libraries are trusted; everything else falls back to
 * upstream's default policy (excalidraw.com + the official libraries repo).
 * Used as `validateLibraryUrl` in useHandleLibrary — keep in sync with
 * ALLOWED_LIBRARY_URLS in packages/excalidraw/data/library.ts on upgrades.
 */
export const validateKhiyarsaLibraryUrl = (libraryUrl: string): boolean => {
  const { origin, hostname, pathname } = new URL(libraryUrl);

  if (
    origin === window.location.origin &&
    pathname.startsWith("/libraries/")
  ) {
    return true;
  }

  if (/(^|\.)excalidraw\.com$/.test(hostname)) {
    return true;
  }

  if (
    hostname === "raw.githubusercontent.com" &&
    pathname.startsWith("/excalidraw/excalidraw-libraries")
  ) {
    return true;
  }

  return false;
};

export const useKhiyarsaDefaultLibraries = (
  excalidrawAPI: ExcalidrawImperativeAPI | null,
) => {
  useEffect(() => {
    if (!excalidrawAPI || !LIBRARY_FILES.length) {
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        if (localStorage.getItem(SEED_FLAG)) {
          return;
        }

        // never seed over an existing library (pre-existing users)
        const persisted = await LibraryIndexedDBAdapter.load();
        if (persisted?.libraryItems?.length) {
          localStorage.setItem(SEED_FLAG, "1");
          return;
        }

        const results = await Promise.allSettled(
          LIBRARY_FILES.map(async (path) => {
            const response = await fetch(path);
            if (!response.ok) {
              throw new Error(`${path}: HTTP ${response.status}`);
            }
            return loadLibraryFromBlob(await response.blob(), "published");
          }),
        );

        let items: LibraryItems = [];
        for (const result of results) {
          if (result.status === "fulfilled") {
            items = mergeLibraryItems(items, result.value);
          }
        }

        for (const result of results) {
          if (result.status === "rejected") {
            console.error("[khiyarsa] library seed failed:", result.reason);
          }
        }

        if (!cancelled && items.length) {
          await excalidrawAPI.updateLibrary({
            libraryItems: items,
            merge: true,
            defaultStatus: "published",
          });
          localStorage.setItem(SEED_FLAG, "1");
        }
      } catch (error) {
        // non-fatal: the app works fine without seeded libraries
        console.error("[khiyarsa] library seeding error:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [excalidrawAPI]);
};
